<script setup>
  import { ref, computed, watch } from 'vue';
  import ModalTextInput from '@/components/controls/ModalTextInput.vue';
  import { carrierGroups, buildCarrierAddress, parseCarrierAddress } from '@/helpers/alertCarriers';
  import { getPhoneDigits, isValidPhone } from '@/helpers/validation';

  const emits = defineEmits(['delete']);

  const props = defineProps({
    device: {
      type: Object,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  });

  const name = ref('');
  const phone = ref('');
  const carrier = ref('');
  const maxMessages = ref(300);
  const validationErrors = ref({});

  const liveValid = computed(() => ({
    phone: isValidPhone(phone.value),
    carrier: !!carrier.value,
  }));

  const errorCount = computed(() => Object.keys(validationErrors.value).length);

  function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return raw;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function validate() {
    const errors = {};
    if (!carrier.value) errors.carrier = 'Carrier is required';
    if (!isValidPhone(phone.value)) errors.phone = 'A valid phone number is required';
    validationErrors.value = errors;
    return Object.keys(errors).length === 0;
  }

  function buildPayload() {
    return {
      name: name.value,
      address: buildCarrierAddress(carrier.value, getPhoneDigits(phone.value)),
      max_message_count: Number(maxMessages.value) || 300,
    };
  }

  watch(() => props.device, (device) => {
    validationErrors.value = {};
    if (device) {
      name.value = device.name || '';
      maxMessages.value = device.max_message_count ?? 300;
      const parsed = parseCarrierAddress(device.address);
      phone.value = formatPhone(parsed.digits);
      carrier.value = parsed.template;
    } else {
      name.value = 'Text Message';
      phone.value = '';
      carrier.value = '';
      maxMessages.value = 300;
    }
  }, { immediate: true });

  defineExpose({ validate, buildPayload, errorCount });
</script>

<template>
  <div>
    <div class="jwx-modal__section">
      <div class="jwx-modal__section-body">
      <ModalTextInput
        v-model="name"
        id="device-mobile-name"
        name="device-mobile-name"
        labelText="Name"
        subText="Max 64 characters"
      />
      <div class="jwx-modal__form-control">
        <label for="device-mobile-carrier" class="jwx-modal__control-label jwx-flex">
          Carrier<span class="jwx-modal__control-required" :data-valid="liveValid.carrier ? 'true' : null">Required</span>
        </label>
        <div class="jwx-modal__input-group" :data-error="validationErrors.carrier ? 'true' : null">
          <div class="jwx-modal__input-wrapper">
            <select
              v-model="carrier"
              id="device-mobile-carrier"
              name="device-mobile-carrier"
              class="jwx-modal__input"
            >
              <option value="" disabled>Select your Carrier...</option>
              <optgroup
                v-for="group in carrierGroups"
                :key="group.country"
                :label="group.country"
              >
                <option
                  v-for="c in group.carriers"
                  :key="c.name + c.template"
                  :value="c.template"
                >{{ c.name }}</option>
              </optgroup>
            </select>
          </div>
        </div>
        <p v-if="validationErrors.carrier" class="jwx-modal__control-error">{{ validationErrors.carrier }}</p>
      </div>
      <ModalTextInput
        v-model="phone"
        id="device-mobile-phone"
        name="device-mobile-phone"
        labelText="Phone Number"
        subText="Enter your local number without country code."
        required
        :errorText="validationErrors.phone"
        :valid="!!liveValid.phone"
      />
      <ModalTextInput
        v-model="maxMessages"
        id="device-mobile-max-messages"
        name="device-mobile-max-messages"
        labelText="Max Messages"
        subText="Your monthly text alerts will be capped at this amount."
      />
      </div>
    </div>
    <div v-if="canDelete" class="jwx-modal__section jwx-p-jumbo jwx-flex jwx-justify-center">
      <button
        type="button"
        class="jwx-button jwx-button--pill"
        data-type="danger"
        @click.prevent="emits('delete')"
      >
        Delete Device
      </button>
    </div>
  </div>
</template>
